import React, { useState, useEffect } from 'react';
import { Select, Loader, Text, Box } from '@mantine/core';
import { useQuery } from '@apollo/client';

import { GET_SALESPEOPLE_QUERY, GET_SALESPERSON_BY_CCODE_QUERY } from '../../../queries';

const SalespersonSelect = ({ onSalespersonSelect, defaultSalespersonCode }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [selectData, setSelectData] = useState([]);

  // Fetch all salespeople
  const { data: allData, loading: allLoading, error: allError } = useQuery(GET_SALESPEOPLE_QUERY);

  // Fetch default salesperson by code if provided
  const { data: defaultData, loading: defaultLoading, error: defaultError } = useQuery(
    GET_SALESPERSON_BY_CCODE_QUERY,
    {
      variables: { code: defaultSalespersonCode },
      skip: !defaultSalespersonCode,
    }
  );

  // Handle errors
  useEffect(() => {
    if (allError) console.error('Error fetching all salespeople:', allError);
    if (defaultError) console.error('Error fetching default salesperson:', defaultError);
  }, [allError, defaultError]);

  // Set select data once all salespeople are fetched
  useEffect(() => {
    if (allData?.salespeople && Array.isArray(allData.salespeople)) {
      const formattedData = allData.salespeople.map(person => ({
        value: person.cCode,
        label: `${person.fullName} (${person.cCode})`,
        person: person // Store the full person object
      }));
      setSelectData(formattedData);
    }
  }, [allData]);

  // Handle default salesperson selection
  useEffect(() => {
    if (defaultSalespersonCode && defaultData?.salespeople?.length > 0 && !selectedValue) {
      const foundSalesperson = defaultData.salespeople[0];
      setSelectedValue(foundSalesperson.cCode);
      onSalespersonSelect(foundSalesperson);
      
      // Add to select data if not already present
      setSelectData((prevData) => {
        if (!prevData.some((item) => item.value === foundSalesperson.cCode)) {
          const newItem = {
            value: foundSalesperson.cCode,
            label: `${foundSalesperson.fullName} (${foundSalesperson.cCode})`,
            person: foundSalesperson
          };
          return [newItem, ...prevData];
        }
        return prevData;
      });
    }
  }, [defaultSalespersonCode, defaultData, onSalespersonSelect, selectedValue]);

  const handleSalespersonSelect = (value) => {
    setSelectedValue(value);
    if (value) {
      const selectedItem = selectData.find(item => item.value === value);
      if (selectedItem?.person) {
        onSalespersonSelect(selectedItem.person);
      }
    } else {
      onSalespersonSelect(null);
    }
  };

  return (
    <Box style={{ minWidth: '350px' }}>
      {(allError || defaultError) && (
        <Text size="sm" c="red" mb="xs">
          {allError?.message || defaultError?.message || 'An error occurred while fetching salespeople.'}
        </Text>
      )}
      
      <Select
        label="Select Salesperson"
        placeholder="Choose a salesperson..."
        value={selectedValue}
        onChange={handleSalespersonSelect}
        data={selectData}
        searchable
        clearable
        nothingFoundMessage="No salespeople found"
        size="sm"
        maxDropdownHeight={300}
        disabled={allLoading || defaultLoading}
        rightSection={
          (allLoading || defaultLoading) ? <Loader size="xs" /> : null
        }
        styles={{
          root: { width: '100%' },
          input: { fontSize: '14px' },
          dropdown: { padding: '4px' },
          option: { padding: '6px 8px' },
        }}
      />
    </Box>
  );
};

export default SalespersonSelect;